import React, { FC, useEffect } from "react";

import { Facility } from "@secondcloset/fulfillment-utils";
import { Fulfillment, Common } from "@secondcloset/types";

import { FormInstance, Form, Select, Input, Radio, notification } from "antd";

import { useFacilityIndex } from "../../../hooks/api/common/useFacilityIndex";
import { useCustomerIndex, useCustomerAddresses } from "../../../hooks/api/fulfillment/customer";
import { useProducts } from "../../../hooks/api/fulfillment/products";
import * as DefaultValues from "../helpers/defaultValues";

type Customer = Fulfillment.Customer;
type Address = Common.BaseAddress;
type Product = Fulfillment.Product;

const { Option } = Select;

interface Props {
  form: FormInstance;
  isVisible: boolean;
  isIkea: boolean;
  organizationID: string;
  setIsLoading: (isloading: boolean) => unknown;
}

const OrderInfoForm: FC<Props> = ({ form, isVisible, isIkea, organizationID, setIsLoading }) => {
  const { customerIndex, fetchCustomerIndex } = useCustomerIndex(organizationID);
  const { customerAddresses, fetchCustomerAddresses } = useCustomerAddresses();
  const { products, fetchProducts } = useProducts();
  const facilityIndex = useFacilityIndex();
  const setDefaultValues = (customers: Customer[], addresses: Address[], products: Product[]) => {
    const customerId = DefaultValues.getDefaultCustomer(customers);
    const addressId = DefaultValues.getDefaultAddress(addresses);
    const productId = DefaultValues.getDefaultProduct(products);
    form.setFieldsValue({
      customer: customerId,
      address: addressId,
      product: productId,
    });
  };

  const fetchFormInfo = async () => {
    const customers = await fetchCustomerIndex({ per_page: 100 });
    const addresses = await fetchCustomerAddresses(customers[0].id);
    const products = await fetchProducts({
      organization_id: organizationID,
      per_page: 100,
    });
    setDefaultValues(customers, addresses, products as any);
  };

  useEffect(() => {
    if (!isVisible) return;
    fetchFormInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  useEffect(() => {
    const isLoading = customerIndex.loading || customerAddresses.loading || products.loading;
    setIsLoading(isLoading);
  }, [customerIndex.loading, customerAddresses.loading, products.loading, setIsLoading]);

  useEffect(() => {
    const error = customerIndex.error || customerAddresses.error || products.error;
    if (!error) return;
    notification.error({
      message: "Error",
      description: error,
    });
  }, [customerIndex.error, customerAddresses.error, products.error]);

  const onCustomerChange = async () => {
    const customerId = form.getFieldValue("customer");
    const addresses = await fetchCustomerAddresses(customerId);
    const addressId = DefaultValues.getDefaultAddress(addresses);
    form.setFieldsValue({
      address: addressId,
    });
  };

  const renderIkeaVersion = () => {
    if (!isIkea) return;
    return (
      <Form.Item name="externalPlatformVersion" label="IKEA Version">
        <Radio.Group>
          <Radio value={1}>V1</Radio>
          <Radio value={2}>V2</Radio>
        </Radio.Group>
      </Form.Item>
    );
  };

  const renderFulfilledFrom = () => {
    const hiddenFacilities = ["yyz1", "yyz2"];

    const facilityList = Facility.getFulfillmentFacilityList(facilityIndex.data || []).filter(
      (facility) => !hiddenFacilities.includes(facility.code)
    );

    return (
      <Form.Item name="fullFilledFrom" label="FullFilled From">
        <Select>
          {facilityList.map((facility) => {
            return (
              <Option key={facility.code} value={facility.code}>
                {facility.name}
              </Option>
            );
          })}
        </Select>
      </Form.Item>
    );
  };

  const renderCustomer = () => {
    const customerList: Customer[] = customerIndex.data ?? [];
    return (
      <Form.Item name="customer" label="Customer">
        <Select
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
        >
          {customerList.map((customer) => {
            return (
              <Option key={customer.id} value={customer.id}>
                {customer.name}
              </Option>
            );
          })}
        </Select>
      </Form.Item>
    );
  };

  const renderAddress = () => {
    const addressList: Address[] = customerAddresses.data ?? [];
    return (
      <Form.Item name="address" label="Address">
        <Select>
          {addressList.map((address) => {
            return (
              <Option key={address.id} value={address.id}>
                {address.address + ", " + address.city + ", " + address.province}
              </Option>
            );
          })}
        </Select>
      </Form.Item>
    );
  };

  const renderProducts = () => {
    const productList: Product[] = (products.data as any) ?? [];
    return (
      <Form.Item name="product" label="Product">
        <Select>
          {productList.map((product) => {
            return (
              <Option key={product.id} value={product.id}>
                {product.name}
              </Option>
            );
          })}
        </Select>
      </Form.Item>
    );
  };

  const renderExternalOrderNumber = () => {
    return (
      <Form.Item name="externalOrderNumber" label="External Order Number">
        <Input />
      </Form.Item>
    );
  };
  return (
    <Form
      form={form}
      name="orderForm"
      onFieldsChange={(change: any[]) => {
        if (change[0].name[0] !== "customer") return; // only refetch when customer change
        onCustomerChange();
      }}
      initialValues={{
        fullFilledFrom: DefaultValues.getDefaultFacilityCode(),
        externalPlatformVersion: 2,
        externalOrderNumber: DefaultValues.getDefaultExternalOrderNumber(),
      }}
    >
      {renderIkeaVersion()}
      {renderFulfilledFrom()}
      {renderCustomer()}
      {renderAddress()}
      {renderProducts()}
      {renderExternalOrderNumber()}
    </Form>
  );
};

export default OrderInfoForm;
